import { useProviderConfigStore } from '../providers/config'
import { useProviderStore } from '../providers/provider'
import { useConsciousnessStore } from './consciousness'
import { useHearingStore } from './hearing'
import { useSpeechStore } from './speech'
import { useVisionStore } from './vision'

/**
 * Leaves module selections unchanged.
 *
 * Hosted official providers are not part of the local desktop. Empty modules
 * stay empty until the user chooses a provider.
 *
 * @returns `false` because this command does not change module state.
 */
export async function configureAsDefaultsIfEmpty(): Promise<boolean> {
  return false
}
/**
 * Removes provider state that is valid only during an authenticated session.
 *
 * The caller must own synchronized Pinia leadership. Custom module selections
 * remain unchanged. An official speech selection falls back to No Speech so
 * the speech pipeline keeps its account-free disabled state after logout.
 *
 * @returns `true` when the command changes module or provider state.
 */
export async function unconfigureAuthenticationProviders(): Promise<boolean> {
  const consciousnessStore = useConsciousnessStore()
  const hearingStore = useHearingStore()
  const speechStore = useSpeechStore()
  const visionStore = useVisionStore()
  const providerStore = useProviderStore()
  const providerConfigStore = useProviderConfigStore()
  let changed = false

  if (providerConfigStore.providers[consciousnessStore.activeProvider]?.configuredBy === 'authentication') {
    consciousnessStore.activeProvider = ''
    consciousnessStore.activeModel = ''
    consciousnessStore.customModelName = ''
    changed = true
  }

  if (providerConfigStore.providers[hearingStore.activeTranscriptionProvider]?.configuredBy === 'authentication') {
    hearingStore.activeTranscriptionProvider = ''
    hearingStore.activeTranscriptionModel = ''
    hearingStore.activeCustomModelName = ''
    changed = true
  }

  if (providerConfigStore.providers[speechStore.activeSpeechProvider]?.configuredBy === 'authentication') {
    speechStore.activeSpeechProvider = 'speech-noop'
    speechStore.activeSpeechModel = ''
    speechStore.activeSpeechVoiceId = ''
    speechStore.activeSpeechVoice = undefined
    changed = true
  }

  if (providerConfigStore.providers[visionStore.activeProvider]?.configuredBy === 'authentication') {
    visionStore.activeProvider = ''
    visionStore.activeModel = ''
    visionStore.customModelName = ''
    changed = true
  }

  for (const [providerId, provider] of Object.entries(providerConfigStore.providers)) {
    if (provider.configuredBy !== 'authentication')
      continue

    const isPersistedAsAdded = providerConfigStore.addedProviders[providerId]
    if (provider.status === 'unconfigured' && !isPersistedAsAdded)
      continue

    await providerStore.setProviderUnconfigured(providerId)
    changed = true
  }

  return changed
}
